/**
 * Bottom inset for the PREFERRED **checkout swipe-up drawer only**
 * (`CheckoutBottomSheet` in `PreferredPaymentView`).
 *
 * DO NOT use for `Common/Drawer`, `ScreenDrawer`, Modal, or any other overlay —
 * those keep their existing layout. This module exists solely so the
 * Apple/Google-Pay-style checkout sheet clears host browser toolbars.
 *
 * Root cause of "Use other payment method" disappearing on mobile Chrome/Safari:
 * the checkout sheet was docked with `bottom: 8px` against the *layout* viewport,
 * while the host clips the bottom of the checkout iframe. A nested browsing
 * context reports its own layout viewport through both `innerHeight` and
 * `visualViewport`, so neither value exposes that external clipping.
 *
 * Fix: observe the sheet's full-viewport root with an implicit-root
 * IntersectionObserver. Browsers expose its cross-origin intersection rectangle
 * without exposing parent DOM, which lets the iframe measure the actually
 * clipped bottom strip. No guessed toolbar height or host-specific signal.
 */

/** Matches Tailwind `bottom-2` — the original float off the viewport edge. */
export const CHECKOUT_DRAWER_BOTTOM_GAP_PX = 8;

export interface CheckoutDrawerBottomInsetInput {
  innerHeight: number;
  visualViewportHeight: number | null;
  visualViewportOffsetTop: number | null;
}

export const getCheckoutDrawerBottomInset = ({
  innerHeight,
  visualViewportHeight,
  visualViewportOffsetTop,
}: CheckoutDrawerBottomInsetInput): number => {
  const hasVisualViewport = visualViewportHeight != null && visualViewportOffsetTop != null;
  const visualViewportBottom = hasVisualViewport
    ? Math.max(0, Math.round(innerHeight - visualViewportHeight - visualViewportOffsetTop))
    : 0;

  return Math.max(CHECKOUT_DRAWER_BOTTOM_GAP_PX, visualViewportBottom);
};

export interface CheckoutDrawerHostIntersectionInput {
  isIntersecting: boolean;
  targetBottom: number;
  intersectionBottom: number;
}

/** Bottom pixels geometrically clipped outside the checkout iframe. */
export const getCheckoutDrawerHostBottomInset = ({
  isIntersecting,
  targetBottom,
  intersectionBottom,
}: CheckoutDrawerHostIntersectionInput): number => {
  if (!isIntersecting || !Number.isFinite(targetBottom) || !Number.isFinite(intersectionBottom)) return 0;
  return Math.max(0, Math.round(targetBottom - intersectionBottom));
};

/** Live visual-viewport read used alongside the host-intersection measurement. */
export const readCheckoutDrawerBottomInset = (): number => {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  return getCheckoutDrawerBottomInset({
    innerHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
    visualViewportHeight: vv?.height ?? null,
    visualViewportOffsetTop: vv?.offsetTop ?? null,
  });
};
